import { Component, OnInit } from '@angular/core';
import { MatSelectChange, MatSnackBar } from '@angular/material';
import { ActivatedRoute, Router } from '@angular/router';

import { AuthenticationService } from '../../../services/authentication.service';
import { ProductService } from '../../../services/product.service';
import { LineService } from 'src/app/services/line.service';
import { FormBuilder, FormGroup, FormArray, Validators } from '@angular/forms';
import { forkJoin } from 'rxjs';
import { CompanyService } from 'src/app/services/company.service';

@Component({
  selector: 'app-edit-product',
  templateUrl: './edit-product.component.html',
  styleUrls: ['./edit-product.component.css']
})

export class EditProductComponent implements OnInit{
  productForm: FormGroup;
  product: any;
  types = ['sales', 'marketing']
  lines: any[];
  companies: any[];
  
  versionForm: FormGroup;
  versionsMenu: any[];
  selectedVersion: any = null;

  iconFile: File | null = null;
  packageFile: File | null = null;
  screenshotFiles: File[] = [];
  pdfFile: File;

  companyAssignmentForm: FormGroup;

  salesFields = [
    'name',
    'price',
  ];

  marketingFields = [
    'name',
    'lineIds',
    'isPortrait',
    'catalogueProducts',
    'competitors',
  ];

  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private snackBar: MatSnackBar,
    private authenticationService: AuthenticationService,
    private productService: ProductService,
    private lineService: LineService,
    private companyService: CompanyService,
    private formBuilder: FormBuilder,
  ) {}

  ngOnInit() {
    this.authenticationService.isLoggedInWithRedirect();
    const productId = this.route.snapshot.paramMap.get('productId');

    this.productForm = this.formBuilder.group({
      name: [''],
      type: [''],
      price: [''],
      catalogue: [''],
      lines: this.formBuilder.array([]),
      catalogueProducts: this.formBuilder.array([]),
      competitors: this.formBuilder.array([]),
      isPortrait: [false],
    });

    this.versionForm = this.formBuilder.group({
      number: [''],
      sections: this.formBuilder.array([]),
    });
    this.addSection();

    this.companyAssignmentForm = this.formBuilder.group({
      company: ['', Validators.required],
    });

    forkJoin([
      this.lineService.getLines(),
      this.productService.getProduct(productId),
      this.companyService.getAllCompanies(),
    ]).subscribe(([lines, product, companies]: [any, any, any]) => {  
      if (lines.message === "success") {
        this.lines = lines.res.lines;
      } else {
        this.router.navigateByUrl('/products');
        return;
      }
  
      if (product.message === "success") {
        this.product = product.res.product;
        
        this.populateProductForm();
      } else {
        this.snackBar.open('Failed to get product', 'Close', {
          verticalPosition: 'top',
          duration: 5000,
        });
        this.router.navigateByUrl('/products');
      }

      if (companies.message === "success") {
        this.companies = companies.res.companies;
        const currentCompany = this.authenticationService.getCompanyId();
        this.companies = this.companies.filter((company: any) => company._id !== currentCompany);
      } else {
        this.snackBar.open('Failed to get companies', 'Close', {
          verticalPosition: 'top',
          duration: 5000,
        });
        this.router.navigateByUrl('/products');
      }
    });
  }

  // #=========== ---- =========== Populate Form Array =========== ---- ===========# //
  populateProductForm(): void {
    this.productForm.get('name').setValue(this.product.name);
    this.productForm.get('type').setValue(this.product.type);

    if (this.product.type === "sales") {
      this.productForm.get('catalogue').setValue(this.product.catalogue);
      this.productForm.get('catalogue').disable();
      this.productForm.get('price').setValue(this.product.price);
    } else {
      this.productForm.get('isPortrait').setValue(this.product.isPortrait);
      this.versionsMenu = this.product.versions
        .map((version: any) => ({ name: `Version ${version.number}`, value: version.number }));

      this.product.lines = [];
      const linesFormArray = this.productForm.get('lines') as FormArray;
      this.lines.forEach((line) => {
        const isSelected = line.products.find((lineProduct: any) => {
          return lineProduct._id === this.product._id
        }) ? true : false;

        isSelected && this.product.lines.push(line._id);
        linesFormArray.push(this.formBuilder.control(isSelected));
      });

      const catalogueProductsFormArray = this.productForm.get('catalogueProducts') as FormArray;
      this.product.catalogueProducts.forEach((catalogueProduct: string) => {
        catalogueProductsFormArray.push(this.formBuilder.control(catalogueProduct));
      });

      const competitorsFormArray = this.productForm.get('competitors') as FormArray;
      this.product.competitors.forEach((competitor: string) => {
        competitorsFormArray.push(this.formBuilder.control(competitor));
      });
    }
  }

  getLatestVersion(): any {
    return this.product.versions.length + 1;
  }

  onSelectVersion(event: MatSelectChange): void {
    const versionNumber = event.value;
    this.selectedVersion = this.product.versions.find((version: any) => version.number === versionNumber);
    
    const sectionsFormArray = this.versionForm.get('sections') as FormArray;
    while (sectionsFormArray.length) {
      sectionsFormArray.removeAt(0);
    }
    
    this.selectedVersion.sections.forEach((section: any) => {
      const sectionGroup = this.formBuilder.group({
        name: section.name,
        slides: this.formBuilder.array([]),
      });
      sectionsFormArray.push(sectionGroup);

      section.slides.forEach((slide: any) => {
        const slidesFormArray = sectionGroup.get('slides') as FormArray;
        slidesFormArray.push(this.formBuilder.group({ name: slide.name }));
      });
    });
  }

  // #=========== ---- =========== Get Type =========== ---- ===========# //
  getType(): string {
    return this.productForm.get('type').value;
  }

  addChip(event: KeyboardEvent, formControlName: string): void {
    event.preventDefault();
    const input = event.target as HTMLInputElement;
    const value = input.value.trim();

    if (value) {
      const formControl = this.productForm.get(formControlName) as FormArray;
      formControl.push(this.formBuilder.control(value));
      input.value = '';
    }
  }

  removeChip(index: number, formControlName: string): void {
    const formControl = this.productForm.get(formControlName) as FormArray;
    formControl.removeAt(index);
  }

  getSectionGroups(): FormArray {
    return this.versionForm.get('sections') as FormArray;
  }

  addSection(): void {
    const sectionGroup = this.formBuilder.group({
      name: [''],
      slides: this.formBuilder.array([])
    });
    this.getSectionGroups().push(sectionGroup);
  }

  removeSection(index: number): void {
    this.getSectionGroups().removeAt(index);
  }

  addSlide(sectionIndex: number, event: KeyboardEvent): void {
    event.preventDefault();
    const input = event.target as HTMLInputElement;
    const value = input.value.trim();

    if (value) {
      const slides = this.getSectionGroups().at(sectionIndex).get('slides') as FormArray;
      slides.push(this.formBuilder.group({ name: value }));
      input.value = '';
    }
  }

  removeSlide(sectionIndex: number, slideIndex: number): void {
    const slides = this.getSectionGroups().at(sectionIndex).get('slides') as FormArray;
    slides.removeAt(slideIndex);
  }

  onFileChange(event: Event, fileType: string): void {
    const input = event.target as HTMLInputElement;
    
    if (input.files && input.files.length > 0) {
      switch (fileType) {
        case 'icon':
          this.iconFile = input.files[0];
          break;
        case 'package':
          this.packageFile = input.files[0];
          break;
        case 'pdf':
          this.pdfFile = input.files[0];
          break;
        case 'screenshots':
          this.screenshotFiles = Array.from(input.files);
          break;
        default:
          return;
      }
    }
  }

  getSelectedLines(): string[] {
    const selectedLines = this.productForm.value.lines;

    return this.lines
      .filter((_, index) => selectedLines[index])
      .map(line => line._id);
  }

  onUpdateProduct() {
    const productData = this.productForm.value;
    const type = productData.type;
    delete productData.type;

    if (type === 'marketing') {
      const lineIds = this.getSelectedLines();

      const lineIdsJSON = JSON.stringify(lineIds);
      const productLinesJSON = JSON.stringify(this.product.lines);
      if (lineIdsJSON !== productLinesJSON) {
        productData.lineIds = lineIds;
      }

      delete productData.lines;
      delete productData.catalogue;
      delete productData.price;
    } else if (type === 'sales') {
      productData.price = parseFloat(productData.price);

      delete productData.catalogue;
      delete productData.catalogueProducts;
      delete productData.competitors;
      delete productData.isPortrait;
      delete productData.type;
      delete productData.lines;
    }

    this.productService
      .updateProduct(this.product._id, type, productData)
      .subscribe((data: any) => {
        if (data.message === "success") {
          this.snackBar.open('Product Updated successfully', 'Close', {
            verticalPosition: 'top',
            duration: 3000,
          });
        } else {
          this.snackBar.open('Failed to update product, try again later', 'Close', {
            verticalPosition: 'top',
            duration: 3000,
          });
        }
        this.router.navigateByUrl('/products');
      });
  }

  onDeleteProduct() {
    this.productService
      .deleteProduct(this.product._id)
      .subscribe((data: any) => {
        if (data.message === "success") {
          this.snackBar.open('Product deleted successfully', 'Close', {
            verticalPosition: 'top',
            duration: 3000,
          });
        } else {
          this.snackBar.open('Failed to delete product', 'Close', {
            verticalPosition: 'top',
            duration: 3000,
          });
        }
        this.router.navigateByUrl('/products');
      });
  }

  onAddVersion() {
    if (!this.packageFile || !this.iconFile) {
      this.snackBar.open('Please upload both package and icon files', 'Close', {
        verticalPosition: 'top',
        duration: 3000,
      });
      return;
    }

    const productData = new FormData();
    productData.append('name', this.product.name);
    
    const version = this.versionForm.value;
    delete version.number;
    productData.append('version', JSON.stringify(version));

    if (this.iconFile) {
      productData.append('icon', this.iconFile);
    }

    if (this.packageFile) {
      const packageBlob = new Blob([this.packageFile], { type: 'application/zip' });
      productData.append('package', packageBlob, this.packageFile.name);
    }

    if (this.pdfFile) {
      const pdfBlob = new Blob([this.pdfFile], { type: 'application/pdf' });
      productData.append('pdf', pdfBlob, this.pdfFile.name);
    }

    if (this.screenshotFiles.length > 0) {
      this.screenshotFiles.forEach(file => { productData.append('screenshots', file) });
    }

    this.productService
      .addVersion(this.product._id, productData)
      .subscribe((data: any) => {
        if (data.message === "success") {
          this.snackBar.open('Version added successfully', 'Close', {
            verticalPosition: 'top',
            duration: 3000,
          });
          this.router.navigateByUrl('/products');
        } else {
          this.snackBar.open('Failed to add version, try again later', 'Close', {
            verticalPosition: 'top',
            duration: 3000,
          });
        }
      });
  }

  onEditVersion() {
    if (!this.packageFile || !this.iconFile) {
      this.snackBar.open('Please upload both package and icon files', 'Close', {
        verticalPosition: 'top',
        duration: 3000,
      });
      return;
    }

    const productData = new FormData();
    productData.append('name', this.product.name);
    productData.append('version', JSON.stringify(this.versionForm.value));

    if (this.iconFile) {
      productData.append('icon', this.iconFile);
    }
    if (this.packageFile) {
      const packageBlob = new Blob([this.packageFile], { type: 'application/zip' });
      productData.append('package', packageBlob, this.packageFile.name);
    }

    if (this.pdfFile) {
      const pdfBlob = new Blob([this.pdfFile], { type: 'application/pdf' });
      productData.append('pdf', pdfBlob, this.pdfFile.name);
    }

    if (this.screenshotFiles.length > 0) {
      this.screenshotFiles.forEach(file => { productData.append('screenshots', file) });
    }

    this.productService
      .editVersion(this.product._id, productData)
      .subscribe((data: any) => {
        if (data.message === "success") {
          this.snackBar.open('Version edited successfully', 'Close', {
            verticalPosition: 'top',
            duration: 3000,
          });
          this.router.navigateByUrl('/products');
        } else {
          this.snackBar.open('Failed to edit version, try again later', 'Close', {
            verticalPosition: 'top',
            duration: 3000,
          });
        }
      });
  }

  onAssignToCompany() {
    const companyId = this.companyAssignmentForm.get('company').value;
    const productId = this.product._id;

    this.productService
      .assignProductToCompany(productId, companyId)
      .subscribe(
        (data: any) => {
          if (data.message === "success") {
            this.snackBar.open('Product assigned to company successfully', 'Close', {
              verticalPosition: 'top',
              duration: 3000,
            });
            this.router.navigateByUrl('/products');
          }
        },
        (error) => {
          if (error.error && error.error.message === "failure") {
            this.snackBar.open(`Failed to assign product to company: ${error.error.details}`, 'Close', {
              verticalPosition: 'top',
              duration: 3000,
            });
          } else {
            this.snackBar.open('An unexpected error occurred', 'Close', {
              verticalPosition: 'top',
              duration: 3000,
            });
          }
        }
      );
  }
}